import { useState, useEffect, useRef } from 'react';
import StorySection from '../story/StorySection';
import StoryAd from '../story/StoryAd';
import { storyService } from '../../lib/supabase-service';
import { useAuth } from '../../lib/auth';
import { ChevronLeft, ChevronRight, Heart, Gift, Image, Mic, MoreHorizontal, X, Pause, Play, ChevronUp } from 'lucide-react';
import { timeAgo } from '../../utils/timeAgo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Define Story interface to match the database schema
interface Story {
  id: string;
  user_id: string;
  media_url: string;
  media_type: 'image' | 'video';
  caption?: string;
  location?: any;
  is_highlighted: boolean;
  metrics: {
    views: number;
    replies: number;
    likes: number;
    gifts_count: number;
    gifts_value: number;
  };
  expires_at: string;
  created_at: string;
  updated_at: string;
  users?: {
    id: string;
    username: string;
    avatar_url?: string;
    verified?: boolean;
  };
  viewed?: boolean;
  duration?: number;
  videoThumbnail?: string;
}

export default function StoryView() {
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [stories, setStories] = useState<Story[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [showAdSkipButton, setShowAdSkipButton] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [showFullDescription, setShowFullDescription] = useState<boolean>(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const { user: currentUser } = useAuth();

  // Fetch real stories from database
  useEffect(() => {
    const fetchStories = async () => {
      try {
        const fetchedStories = await storyService.getStories(20);
        
        // Process stories and add advertisements
        const processedStories: Story[] = [];
        
        // Group stories by user
        const userStories: Record<string, Story[]> = {};
        fetchedStories.forEach((story: any) => {
          const userId = story.user_id;
          if (!userStories[userId]) {
            userStories[userId] = [];
          }
          
          // Transform the story data to match our interface
          const transformedStory: Story = {
            id: story.id,
            user_id: story.user_id,
            media_url: story.media_url,
            media_type: story.media_type as 'image' | 'video',
            caption: story.caption,
            location: story.location,
            is_highlighted: story.is_highlighted,
            metrics: story.metrics || { views: 0, replies: 0, likes: 0, gifts_count: 0, gifts_value: 0 },
            expires_at: story.expires_at,
            created_at: story.created_at,
            updated_at: story.updated_at,
            users: story.user,
            viewed: false, // TODO: Track viewed status
            duration: story.media_type === 'video' ? 15 : 5, // Default durations
            videoThumbnail: story.media_type === 'video' ? story.media_url : undefined
          };
          
          userStories[userId].push(transformedStory);
        });

        // Add stories to processed list, inserting ads every 3-4 user groups
        let userCount = 0;
        Object.values(userStories).forEach(userStoriesGroup => {
          userStoriesGroup.forEach(story => {
            processedStories.push(story);
          });
          
          userCount++;
          // Insert ad after every 3-4 users
          if (userCount % 3 === 0) {
            // Create a mock ad story - you can replace this with real ad data
            const adStory: Story = {
              id: `ad-${userCount}`,
              user_id: 'ad-user',
              media_url: 'https://cdn.pixabay.com/photo/2016/11/22/19/08/hangers-1850082_1280.jpg',
              media_type: 'image',
              caption: 'Sponsored Content',
              is_highlighted: false,
              metrics: { views: 0, replies: 0, likes: 0, gifts_count: 0, gifts_value: 0 },
              expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              users: {
                id: 'ad-user',
                username: 'Sponsored',
                avatar_url: 'https://cdn.pixabay.com/photo/2016/11/22/19/08/hangers-1850082_1280.jpg',
                verified: false
              },
              viewed: false,
              duration: 5
            };
            processedStories.push(adStory);
          }
        });

        setStories(processedStories);
      } catch (error) {
        console.error('Error fetching stories:', error);
        setStories([]); // Set empty array on error
      }
    };

    if (currentUser) {
      fetchStories();
    }
  }, [currentUser]);

  // Handle story click from StorySection
  const handleStoryClick = (storyId: string) => {
    const story = stories.find(s => s.id === storyId);
    if (story) {
      setSelectedStory(story);
      setProgress(0);
      setIsPaused(false);
      
      // Track story view
      if (story.id !== `ad-${story.id}` && !story.id.startsWith('ad-')) {
        storyService.trackStoryView(story.id).catch(console.error);
      }
    }
  };

  // Handle add story click
  const handleAddStoryClick = () => {
    console.log('Add story clicked');
    // TODO: Implement story creation flow
  };

  // Handle post submit
  const handlePostSubmit = (content: string, mediaType: string, privacy: string) => {
    console.log('Post submit:', { content, mediaType, privacy });
    // TODO: Implement post creation
  };

  const closeStoryView = () => {
    setSelectedStory(null);
    setProgress(0);
    setIsPaused(false);
  };

  // Navigate to next story
  const nextStory = () => {
    if (!selectedStory) return;
    
    const currentIndex = stories.findIndex(s => s.id === selectedStory.id);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < stories.length) {
      const nextStory = stories[nextIndex];
      setSelectedStory(nextStory);
      setProgress(0);
      setIsPaused(false);
      
      // Track story view
      if (!nextStory.id.startsWith('ad-')) {
        storyService.trackStoryView(nextStory.id).catch(console.error);
      }
    } else {
      closeStoryView();
    }
  };

  // Navigate to previous story
  const prevStory = () => {
    if (!selectedStory) return;
    
    const currentIndex = stories.findIndex(s => s.id === selectedStory.id);
    const prevIndex = currentIndex - 1;
    
    if (prevIndex >= 0) {
      const prevStory = stories[prevIndex];
      setSelectedStory(prevStory);
      setProgress(0);
      setIsPaused(false);
      
      // Track story view
      if (!prevStory.id.startsWith('ad-')) {
        storyService.trackStoryView(prevStory.id).catch(console.error);
      }
    }
  };

  // Auto-progress story
  useEffect(() => {
    if (!selectedStory || isPaused) return;

    const duration = selectedStory.duration || 5;
    const interval = 50; // Update every 50ms for smooth progress
    const increment = (100 / (duration * 1000)) * interval;

    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          // Check if it's an ad and show skip button
          if (selectedStory.id.startsWith('ad-') && !showAdSkipButton) {
            setShowAdSkipButton(true);
            return 100;
          }
          
          // Auto advance to next story
          nextStory();
          return 0;
        }
        return prev + increment;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [selectedStory, isPaused]);

  // Convert stories to format expected by StorySection
  const storiesForSection = stories.map(story => ({
    id: story.id,
    user: {
      id: story.users?.id || story.user_id,
      username: story.users?.username || 'Unknown',
      avatar_url: story.users?.avatar_url,
      verified: story.users?.verified || false
    },
    viewed: story.viewed || false
  }));

  // Current user for story section
  const currentStoryUser = currentUser ? {
    id: currentUser.id,
    username: currentUser.user_metadata?.username || currentUser.email?.split('@')[0] || 'User',
    avatar_url: currentUser.user_metadata?.avatar_url,
    verified: false
  } : {
    id: 'guest',
    username: 'Guest',
    verified: false
  };

  return (
    <>
      <StorySection
        currentUser={currentStoryUser}
        stories={storiesForSection}
        onStoryClick={handleStoryClick}
        onAddStoryClick={handleAddStoryClick}
        onPostSubmit={handlePostSubmit}
      />

      {/* Story View Modal */}
      {selectedStory && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Progress bar */}
          <div className="absolute top-4 left-4 right-4 z-30">
            <div className="w-full bg-gray-600 rounded-full h-1">
              <div 
                className="bg-white h-1 rounded-full transition-all duration-100 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Top controls - Avatar/Name on left, 3 dots and X on right */}
          <div className="absolute top-10 left-4 right-4 z-30 flex items-center justify-between">
            {/* User info on left */}
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setIsPaused(true)}
              title={`View ${selectedStory.users?.username || 'Unknown'}'s profile`}
            >
              <Avatar className="w-10 h-10 border-2 border-white">
                <AvatarImage src={selectedStory.users?.avatar_url} alt={selectedStory.users?.username || 'Unknown'} />
                <AvatarFallback>{(selectedStory.users?.username || 'Unknown')[0].toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-white font-medium text-sm">{selectedStory.users?.username || 'Unknown'}</span>
                  {selectedStory.users?.verified && (
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
                <span className="text-gray-300 text-xs">{timeAgo(selectedStory.created_at)}</span>
              </div>
            </div>

            {/* Controls on right */}
            <div className="flex gap-2">
              <button 
                onClick={() => console.log('Menu clicked')}
                className="bg-black/40 backdrop-blur-sm text-white p-2 rounded-lg hover:bg-[#065900] transition-colors"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
              <button 
                onClick={closeStoryView}
                className="bg-black/40 backdrop-blur-sm text-white p-2 rounded-lg hover:bg-[#065900] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Centered horizontal navigation arrows with skip ad button below right arrow */}
          <div className="absolute left-0 right-0 top-1/2 transform -translate-y-1/2 flex justify-between px-4 pointer-events-none">
            <button 
              onClick={prevStory} 
              className="bg-black/40 backdrop-blur-sm text-white p-3 rounded-full hover:bg-[#065900] transition-colors z-20 pointer-events-auto"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <div className="flex flex-col items-center gap-3 pointer-events-auto">
              <button 
                onClick={nextStory} 
                className="bg-black/40 backdrop-blur-sm text-white p-3 rounded-full hover:bg-[#065900] transition-colors z-20"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              {showAdSkipButton && (
                <button 
                  onClick={nextStory} 
                  className="bg-white text-black text-sm font-medium px-4 py-1 rounded-full z-20 hover:bg-gray-100 transition-colors"
                >
                  Skip Ad
                </button>
              )}
            </div>
          </div>

          {/* Story content */}
          {selectedStory.users?.username === 'Sponsored' ? (
            <StoryAd onAction={nextStory} />
          ) : (
            <div className="relative w-full h-full">
              {/* Left click area for previous story */}
              <div 
                className="absolute left-0 top-0 bottom-0 w-1/3 z-10 cursor-pointer" 
                onClick={(e) => {
                  e.stopPropagation();
                  prevStory();
                }}
              />
              
              {/* Center click area for pause/play */}
              <div 
                className="absolute left-1/3 right-1/3 top-0 bottom-0 z-10 cursor-pointer" 
                onClick={() => setIsPaused(!isPaused)}
              />
              
              {/* Right click area for next story */}
              <div 
                className="absolute right-0 top-0 bottom-0 w-1/3 z-10 cursor-pointer" 
                onClick={(e) => {
                  e.stopPropagation();
                  nextStory();
                }}
              />
              
              {selectedStory.media_type === 'image' ? (
                <img src={selectedStory.media_url} alt={selectedStory.users?.username || 'Unknown'} className="w-full h-full object-cover" />
              ) : (
                <video 
                  src={selectedStory.media_url} 
                  poster={selectedStory.videoThumbnail} 
                  className="w-full h-full object-cover" 
                  autoPlay={!isPaused}
                  loop={false}
                  muted
                />
              )}
              
              {/* Play/Pause overlay */}
              {isPaused && (
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <div className="bg-black/60 backdrop-blur-sm p-5 rounded-full">
                    {isPaused ? <Play className="h-12 w-12 text-white" /> : <Pause className="h-12 w-12 text-white" />}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bottom content area */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-20">
            {/* Description section above comment input with view more dropup */}
            <div className="mb-4 text-center relative">
              <div 
                ref={descriptionRef}
                className={`text-white text-sm ${!showFullDescription ? 'line-clamp-3' : ''} mb-1`}
              >
                {selectedStory.caption}
              </div>
              {/* Full description dropup */}
              {showFullDescription && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-black/80 backdrop-blur-sm rounded-lg p-4 text-white text-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Description</span>
                    <button 
                      onClick={() => setShowFullDescription(false)}
                      className="text-gray-300 hover:text-white"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-left">{selectedStory.caption}</p>
                </div>
              )}
            </div>

            {/* Comment input section */}
            <div className="flex items-center gap-3">
              {/* User avatar - rounded square */}
              <Avatar className="w-10 h-10 rounded-lg border-2 border-white">
                <AvatarImage src={currentStoryUser.avatar_url} alt={currentStoryUser.username} />
                <AvatarFallback>{currentStoryUser.username[0].toUpperCase()}</AvatarFallback>
              </Avatar>
              
              {/* Comment input container with embedded buttons */}
              <div className="flex-1 bg-black/40 backdrop-blur-sm border border-gray-600 rounded-lg px-4 py-2 flex items-center gap-3">
                <button 
                  onClick={() => console.log('Photo clicked')}
                  className="text-gray-400 hover:text-[#065900] transition-colors"
                >
                  <Image className="h-5 w-5" />
                </button>
                <button 
                  onClick={() => console.log('Voice clicked')}
                  className="text-gray-400 hover:text-[#065900] transition-colors"
                >
                  <Mic className="h-5 w-5" />
                </button>
                <input
                  type="text"
                  placeholder="Add a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="flex-1 bg-transparent text-white placeholder-gray-400 outline-none text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && console.log('Comment posted:', comment)}
                />
              </div>

              {/* Action buttons */}
              <button 
                onClick={() => console.log('Like clicked')}
                className="bg-black/40 backdrop-blur-sm border border-gray-600 text-white p-2 rounded-lg hover:bg-[#065900] hover:border-[#065900] transition-colors"
              >
                <Heart className="h-5 w-5" />
              </button>
              <button 
                onClick={() => console.log('Gift clicked')}
                className="bg-black/40 backdrop-blur-sm border border-gray-600 text-white p-2 rounded-lg hover:bg-[#065900] hover:border-[#065900] transition-colors"
              >
                <Gift className="h-5 w-5" />
              </button>
              <button 
                onClick={() => console.log('Post clicked')}
                className="bg-[#065900] text-white px-4 py-2 rounded-lg hover:bg-[#054700] transition-colors text-sm font-medium"
              >
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
